import { Component, Input, OnInit, ViewChild } from '@angular/core';

import { ModalService } from '../../../../../../services/modal.service';
import { ModalContainerComponent } from '../../../../../../components/modals/modal-container.component';
import { IModal } from '../../../../../../components/modals/modal.interface';
import { IModalDialogInputData } from '../../../../../../interfaces/modal-dialog.interface';
import { IScanSummaryAllItem } from '../../../../../../../../shared/interfaces/scan-summaries.interface';
import { AccessibilityAuditToolNames, AuditToolNameToAuditType } from '../../../../../../../../shared/constants/audit-tool';
import { TranslateService } from '../../../../../../translate/translate.service';
import {
  ITableColumn,
  ITableConfig,
  ITableRow,
  SortEvent,
} from '../../../../../../components/table/ngb-table/utilities/ngb-table.interface';
import { $scanSummaries } from '../../../../../../../../shared/constants/scan-summaries';
import { IAuditRule, IAuditStandard } from '../../../../../../../../shared/interfaces/audit-rule.interface';
import { IScanAudit } from '../../../../../../../../shared/interfaces/scan-audit.interface';
import { $scanAudit } from '../../../../../../../../shared/constants/scan-audit';
import { $auditRule } from '../../../../../../../../shared/constants/audit-rule';
import { NgbTableUtilities } from '../../../../../../components/table/ngb-table/utilities/ngb-table.utilities';
import { $sortingOrder } from '../../../../../../../../shared/constants/sort';
import { $auditIssuesView } from '../../../../../../../../shared/constants/audit-issues-view';
import {
  CriteriaPreset,
  Separator,
  SuccessCriteriaFormatterService,
} from '../../../../../../services/success-criteria-formatter.service';

interface IIgnoredRuleData {
  [$auditIssuesView.ruleDescription]: string;
  [$auditIssuesView.successCriteriaConformanceLevel]: IAuditStandard[];
  [$auditIssuesView.successCriteriaIdentifier]: string[];
}

enum tableHeaders {
  ruleDescription = 'ruleDescription',
  successCriteriaConformanceLevel = 'successCriteriaConformanceLevel',
}

@Component({
  selector: 'app-excluded-rules-modal',
  templateUrl: './excluded-rules-modal.component.html',
  styleUrls: ['./excluded-rules-modal.component.scss'],
  standalone: false,
})
export class ExcludedRulesModalComponent implements OnInit, IModal {
  @Input() public modalInputData: IModalDialogInputData<{
    scanSummary: IScanSummaryAllItem;
    auditTool: AccessibilityAuditToolNames;
  }>;
  @ViewChild(ModalContainerComponent, { static: true })
  public container: ModalContainerComponent;

  public tableConfig: ITableConfig;
  public tableData: ITableRow[];

  public entriesPerPage: number;
  public entriesAmounts: number[];
  public page: number;
  public ignoredRuleData: IIgnoredRuleData[];

  constructor(
    private modalService: ModalService,
    private translateService: TranslateService,
    private successCriteriaFormatterService: SuccessCriteriaFormatterService,
  ) {
    this.entriesAmounts = [5, 10, 20];
    this.entriesPerPage = this.entriesAmounts[1];
    this.page = 1;
    this.tableConfig = this.getTableConfig();
    this.tableData = [];
    this.ignoredRuleData = [];
  }

  private getTableConfig(): ITableConfig {
    const allColumns: { [key: string]: ITableColumn } = {
      [tableHeaders.ruleDescription]: {
        translationKey: 'table_header_rule_description',
        sortingEnabled: true,
        styles: {
          maxWidth: '50%',
          width: '50%',
        },
      },
      [tableHeaders.successCriteriaConformanceLevel]: {
        translationKey: 'table_header_sc_conformance_level',
        sortingEnabled: true,
      },
    };

    return {
      caption: this.translateService.instant('exclude_rules_modal_title'),
      columns: allColumns,
      emptyState: {
        iconId: 'file-tray',
        title: this.translateService.instant('there_are_no_excluded_findings'),
        subtitle: '',
      },
    };
  }

  private getSuccessCriteriaInfo(identifiers: string[]): string {
    return this.successCriteriaFormatterService.toDisplayCriterias({
      identifiers: identifiers,
      separator: Separator.newLine,
      preset: CriteriaPreset.criteriaFull,
    });
  }

  private toTableRows(rawData: IIgnoredRuleData[]): ITableRow[] {
    const toTableRow = (entry: IIgnoredRuleData): ITableRow => {
      return {
        data: {
          [$auditIssuesView.ruleDescription]: NgbTableUtilities.textCell({
            text: entry[$auditIssuesView.ruleDescription],
          }),
          [$auditIssuesView.successCriteriaConformanceLevel]: NgbTableUtilities.textCell({
            text: this.getSuccessCriteriaInfo(entry[$auditIssuesView.successCriteriaIdentifier]),
            classes: ['pre-line'],
          }),
        },
      };
    };

    return rawData.map(toTableRow);
  }

  private getComparable(column: string, rawDatum: IIgnoredRuleData): string {
    switch (column) {
      case $auditIssuesView.successCriteriaConformanceLevel:
        return this.getSuccessCriteriaInfo(rawDatum[$auditIssuesView.successCriteriaIdentifier]);
      default:
        return String(rawDatum[column]);
    }
  }

  private extractData(summary: IScanSummaryAllItem, tool: AccessibilityAuditToolNames): IIgnoredRuleData[] {
    const data: IIgnoredRuleData[] = [];
    const scanAuditsObj: IScanAudit = summary[$scanSummaries.scanAuditsId] as IScanAudit;
    const scanAuditRules: Record<string, IAuditRule> = scanAuditsObj[$scanAudit.audits][AuditToolNameToAuditType[tool]].rules;

    for (const ruleInfo of Object.values(scanAuditRules)) {
      if (ruleInfo.isSelectedForScanning === true) {
        continue;
      }

      const ruleTitle: string = ruleInfo[$auditRule.title];
      const successCriteriaInfo: IAuditStandard[] = ruleInfo[$auditRule.standards];
      const successCriteriaIdentifier: string[] = ruleInfo[$auditRule.wcagCriteria];

      const issue: IIgnoredRuleData = {
        [$auditIssuesView.ruleDescription]: ruleTitle,
        [$auditIssuesView.successCriteriaConformanceLevel]: successCriteriaInfo,
        [$auditIssuesView.successCriteriaIdentifier]: successCriteriaIdentifier,
      };

      data.push(issue);
    }

    return data;
  }

  public close(): void {
    this.modalService.closeModal();
  }

  public getPaginationLabel(page: number, entriesPerPage: number, total: number): string {
    return this.translateService.instant('n_of_t', [
      `${Math.min((page - 1) * entriesPerPage + 1, total)} - ${Math.min(page * entriesPerPage, total)}`,
      String(total),
    ]);
  }

  public onTableSort({ column, direction }: SortEvent): void {
    const sortFunction = (a: IIgnoredRuleData, b: IIgnoredRuleData): number => {
      const aValue: string = this.getComparable(column, a);
      const bValue: string = this.getComparable(column, b);

      return direction === $sortingOrder.asc
        ? aValue.localeCompare(bValue, 'en-US', { numeric: true, sensitivity: 'base' })
        : bValue.localeCompare(aValue, 'en-US', { numeric: true, sensitivity: 'base' });
    };

    if (direction === $sortingOrder.all) {
      this.tableData = this.toTableRows(this.ignoredRuleData);
    } else {
      this.tableData = this.toTableRows([...this.ignoredRuleData].sort(sortFunction));
    }
  }

  public ngOnInit(): void {
    this.ignoredRuleData = this.extractData(this.modalInputData.data.scanSummary, this.modalInputData.data.auditTool);
    this.tableData = this.toTableRows(this.ignoredRuleData);
  }
}
